(() => {

class ProductImageZoom extends HTMLElement {
  connectedCallback() {
    this.surface = this.querySelector('[data-zoom-surface]');
    this.image = this.querySelector('.product-media__image');
    this.lens = this.querySelector('[data-zoom-lens]');
    this.mode = this.dataset.zoomMode || 'inner';
    this.scale = Math.max(1.5, Number(this.dataset.zoomScale) || 2.5);
    this.source = this.dataset.zoomSource || this.image?.currentSrc || this.image?.src || '';
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.locked = false;
    this.active = false;
    this.lastPoint = null;

    if (!this.surface || !this.image) return;

    this.onPointerEnter = (event) => this.handlePointerEnter(event);
    this.onPointerMove = (event) => this.handlePointerMove(event);
    this.onPointerLeave = () => this.handlePointerLeave();
    this.onClick = (event) => this.handleClick(event);
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onFocus = () => this.handleFocus();
    this.onBlur = () => this.handleBlur();
    this.onViewportChange = () => this.deactivate();

    this.surface.addEventListener('pointerenter', this.onPointerEnter);
    this.surface.addEventListener('pointermove', this.onPointerMove);
    this.surface.addEventListener('pointerleave', this.onPointerLeave);
    this.surface.addEventListener('click', this.onClick);
    this.surface.addEventListener('keydown', this.onKeydown);
    this.surface.addEventListener('focus', this.onFocus);
    this.surface.addEventListener('blur', this.onBlur);
  }

  disconnectedCallback() {
    this.surface?.removeEventListener('pointerenter', this.onPointerEnter);
    this.surface?.removeEventListener('pointermove', this.onPointerMove);
    this.surface?.removeEventListener('pointerleave', this.onPointerLeave);
    this.surface?.removeEventListener('click', this.onClick);
    this.surface?.removeEventListener('keydown', this.onKeydown);
    this.surface?.removeEventListener('focus', this.onFocus);
    this.surface?.removeEventListener('blur', this.onBlur);
    this.removeViewportListeners();
    this.outerPane?.remove();
  }

  handlePointerEnter(event) {
    if (!this.finePointer) return;
    this.activateAt(event.clientX, event.clientY);
  }

  handlePointerMove(event) {
    if (!this.finePointer) return;
    this.activateAt(event.clientX, event.clientY);
  }

  handlePointerLeave() {
    if (!this.locked) this.deactivate();
  }

  handleClick(event) {
    event.preventDefault();
    this.locked = !this.locked;

    if (this.locked) {
      const rect = this.surface.getBoundingClientRect();
      this.activateAt(rect.left + rect.width / 2, rect.top + rect.height / 2);
    } else {
      this.deactivate();
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.locked = false;
    this.deactivate();
  }

  handleFocus() {
    if (this.active) return;
    const rect = this.surface.getBoundingClientRect();
    this.activateAt(rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  handleBlur() {
    this.locked = false;
    this.deactivate();
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  activateAt(clientX, clientY) {
    if (this.isMobileViewport()) return;

    const rect = this.surface.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const y = Math.min(Math.max(clientY - rect.top, 0), rect.height);
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;
    const effectiveMode = this.finePointer ? this.mode : 'inner';

    this.lastPoint = { clientX, clientY };
    this.active = true;
    this.classList.add('is-zoomed');
    this.style.setProperty('--product-image-zoom-scale', String(this.scale));
    this.style.setProperty('--product-image-zoom-x', `${xPercent}%`);
    this.style.setProperty('--product-image-zoom-y', `${yPercent}%`);

    if (effectiveMode === 'circle') {
      this.updateCircleLens(x, y, rect);
    } else if (effectiveMode === 'outer') {
      this.updateOuterPane(xPercent, yPercent, rect);
    }
  }

  updateCircleLens(x, y, rect) {
    if (!this.lens || !this.source) return;

    const lensSize = this.lens.offsetWidth || 170;
    this.lens.hidden = false;
    this.lens.style.left = `${x}px`;
    this.lens.style.top = `${y}px`;
    this.lens.style.backgroundImage = `url("${this.source.replace(/"/g, '\\"')}")`;
    this.lens.style.backgroundSize = `${rect.width * this.scale}px ${rect.height * this.scale}px`;
    this.lens.style.backgroundPosition = `${lensSize / 2 - x * this.scale}px ${lensSize / 2 - y * this.scale}px`;
  }

  updateOuterPane(xPercent, yPercent, rect) {
    if (!this.source) return;

    if (!this.outerPane) {
      this.outerPane = document.createElement('div');
      this.outerPane.className = 'product-image-zoom__outer-pane';
      this.outerPane.hidden = true;
      this.outerPane.setAttribute('aria-hidden', 'true');
      document.body.append(this.outerPane);
    }

    const gutter = 16;
    const paneWidth = Math.min(Math.max(rect.width, 320), Math.max(320, window.innerWidth * 0.42));
    const paneHeight = Math.min(rect.height, window.innerHeight - gutter * 2);
    let left = rect.right + gutter;

    if (left + paneWidth > window.innerWidth - gutter) {
      left = rect.left - paneWidth - gutter;
    }

    if (left < gutter) {
      left = Math.max(gutter, (window.innerWidth - paneWidth) / 2);
    }

    const top = Math.min(
      Math.max(rect.top, gutter),
      Math.max(gutter, window.innerHeight - paneHeight - gutter),
    );

    this.outerPane.hidden = false;
    this.outerPane.style.width = `${paneWidth}px`;
    this.outerPane.style.height = `${paneHeight}px`;
    this.outerPane.style.left = `${left}px`;
    this.outerPane.style.top = `${top}px`;
    this.outerPane.style.backgroundImage = `url("${this.source.replace(/"/g, '\\"')}")`;
    this.outerPane.style.backgroundSize = `${this.scale * 100}%`;
    this.outerPane.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
    this.addViewportListeners();
  }

  addViewportListeners() {
    if (this.viewportListenersActive) return;
    this.viewportListenersActive = true;
    window.addEventListener('scroll', this.onViewportChange, true);
    window.addEventListener('resize', this.onViewportChange);
  }

  removeViewportListeners() {
    if (!this.viewportListenersActive) return;
    this.viewportListenersActive = false;
    window.removeEventListener('scroll', this.onViewportChange, true);
    window.removeEventListener('resize', this.onViewportChange);
  }

  deactivate() {
    this.active = false;
    this.classList.remove('is-zoomed');
    if (this.lens) this.lens.hidden = true;
    if (this.outerPane) this.outerPane.hidden = true;
    this.removeViewportListeners();
  }
}

if (!customElements.get('product-image-zoom')) {
  customElements.define('product-image-zoom', ProductImageZoom);
}

class ProductMediaLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-lightbox-dialog]');
    this.viewport = this.querySelector('[data-lightbox-viewport]');
    this.templates = Array.from(this.querySelectorAll('[data-lightbox-template]'));
    this.thumbnails = Array.from(this.querySelectorAll('[data-lightbox-thumbnail]'));
    this.previousButton = this.querySelector('[data-lightbox-previous]');
    this.nextButton = this.querySelector('[data-lightbox-next]');
    this.counter = this.querySelector('[data-lightbox-counter]');
    this.root = this.closest('main-product') || document;
    this.activeIndex = 0;
    this.lastTrigger = null;

    if (!this.dialog || !this.viewport || this.templates.length === 0) return;

    this.onRootClick = (event) => this.handleRootClick(event);
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onClose = () => this.handleClose();

    this.root.addEventListener('click', this.onRootClick);
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('close', this.onClose);
  }

  disconnectedCallback() {
    this.root?.removeEventListener('click', this.onRootClick);
    this.dialog?.removeEventListener('keydown', this.onKeydown);
    this.dialog?.removeEventListener('close', this.onClose);
  }

  handleRootClick(event) {
    const openButton = event.target.closest('[data-media-lightbox-open]');
    if (openButton && openButton.getAttribute('aria-controls') === this.dialog.id) {
      event.preventDefault();
      this.open(Number(openButton.dataset.mediaIndex) || 0, openButton);
      return;
    }

    if (!this.dialog.open) return;

    const closeButton = event.target.closest('[data-lightbox-close]');
    if (closeButton) {
      event.preventDefault();
      this.dialog.close();
      return;
    }

    const previousButton = event.target.closest('[data-lightbox-previous]');
    if (previousButton) {
      event.preventDefault();
      this.show(this.activeIndex - 1);
      return;
    }

    const nextButton = event.target.closest('[data-lightbox-next]');
    if (nextButton) {
      event.preventDefault();
      this.show(this.activeIndex + 1);
      return;
    }

    const thumbnail = event.target.closest('[data-lightbox-thumbnail]');
    if (thumbnail) {
      event.preventDefault();
      this.show(Number(thumbnail.dataset.mediaIndex) || 0);
      return;
    }

    if (event.target === this.dialog) {
      this.dialog.close();
    }
  }

  handleKeydown(event) {
    const isRtl = getComputedStyle(this).direction === 'rtl';

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.show(this.activeIndex + (isRtl ? 1 : -1));
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.show(this.activeIndex + (isRtl ? -1 : 1));
        break;
      case 'Home':
        event.preventDefault();
        this.show(0);
        break;
      case 'End':
        event.preventDefault();
        this.show(this.templates.length - 1);
        break;
      default:
        break;
    }
  }

  open(index, trigger) {
    this.lastTrigger = trigger;
    this.show(index, false);
    if (!this.dialog.open) this.dialog.showModal();
    requestAnimationFrame(() => this.querySelector('[data-lightbox-close]')?.focus());
  }

  show(index, moveFocus = true) {
    const nextIndex = Math.min(Math.max(index, 0), this.templates.length - 1);
    const template = this.templates[nextIndex];
    if (!template) return;

    this.pauseCurrentMedia();
    this.viewport.replaceChildren(template.content.cloneNode(true));
    this.viewport.setAttribute('aria-label', template.dataset.mediaLabel || '');
    this.activeIndex = nextIndex;

    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const active = thumbnailIndex === nextIndex;
      thumbnail.classList.toggle('is-active', active);
      if (active) {
        thumbnail.setAttribute('aria-current', 'true');
        thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });

    if (this.previousButton) this.previousButton.disabled = nextIndex === 0;
    if (this.nextButton) this.nextButton.disabled = nextIndex === this.templates.length - 1;
    if (this.counter) this.counter.textContent = `${nextIndex + 1} / ${this.templates.length}`;

    this.initializeModelViewer();
    if (moveFocus) this.viewport.focus({ preventScroll: true });
  }

  pauseCurrentMedia() {
    this.viewport?.querySelectorAll('video').forEach((video) => video.pause());
  }

  handleClose() {
    this.pauseCurrentMedia();
    this.viewport.replaceChildren();
    this.lastTrigger?.focus({ preventScroll: true });
    this.lastTrigger = null;
  }

  initializeModelViewer() {
    if (!this.viewport.querySelector('model-viewer') || !window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (error) => {
          if (error) console.error(error);
        },
      },
    ]);
  }
}

if (!customElements.get('product-media-lightbox')) {
  customElements.define('product-media-lightbox', ProductMediaLightbox);
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbnailsContainer = this.querySelector('[data-gallery-thumbnails]');
    this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));
    this.previousButton = this.querySelector('[data-gallery-previous]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.status = this.querySelector('[data-gallery-status]');
    this.xrButton = this.querySelector('[data-gallery-xr-button]');

    if (!this.viewport || this.slides.length === 0) return;

    this.activeIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active')),
    );
    this.scrollFrame = null;

    this.isGridStyleLayout = ['grid', 'grid_2', 'stacked'].includes(this.dataset.layout);
    this.desktopQuery = this.isGridStyleLayout ? window.matchMedia('(min-width: 990px)') : null;
    this.onDesktopQueryChange = () => this.updateState(this.activeIndex, false);
    this.desktopQuery?.addEventListener('change', this.onDesktopQueryChange);

    this.onPreviousClick = () => this.show(this.activeIndex - 1);
    this.onNextClick = () => this.show(this.activeIndex + 1);
    this.onViewportScroll = () => this.handleScroll();
    this.onViewportKeydown = (event) => this.handleKeydown(event);
    this.onThumbnailClick = (event) => {
      const button = event.target.closest('[data-gallery-thumbnail]');
      if (!button) return;
      this.show(Number(button.dataset.mediaIndex), true);
    };

    this.previousButton?.addEventListener('click', this.onPreviousClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.viewport.addEventListener('scroll', this.onViewportScroll, { passive: true });
    this.viewport.addEventListener('keydown', this.onViewportKeydown);
    this.addEventListener('click', this.onThumbnailClick);

    this.resizeObserver = new ResizeObserver(() => {
      this.scrollToIndex(this.activeIndex, 'auto');
    });
    this.resizeObserver.observe(this.viewport);

    /*
     * preserveGalleryElement() (main-product.js, MainProduct class) keeps
     * this exact element across an AJAX variant swap by briefly moving it
     * through a detached document fragment before reinserting it - which
     * disconnects then reconnects it even though its content and scroll
     * position never actually reset. Without this guard, that reconnect
     * replays the same initial scroll below, and if it lands while
     * showMedia()'s smooth thumbnail scroll is still mid-flight, the
     * thumbnail rail visibly restarts - reads as the scroll running twice.
     * Only the very first connect (real page load) needs to force the
     * initial position; later reconnects are already correct as-is.
     */
    if (!this.dataset.galleryInitialized) {
      this.dataset.galleryInitialized = 'true';

      requestAnimationFrame(() => {
        this.scrollToIndex(this.activeIndex, 'auto');
        this.updateState(this.activeIndex, false);
      });
    }
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.onPreviousClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.viewport?.removeEventListener('scroll', this.onViewportScroll);
    this.viewport?.removeEventListener('keydown', this.onViewportKeydown);
    this.removeEventListener('click', this.onThumbnailClick);
    this.desktopQuery?.removeEventListener('change', this.onDesktopQueryChange);
    this.resizeObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  handleScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const viewportRect = this.viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.activeIndex) {
        this.updateState(closestIndex, true);
      }
    });
  }

  handleKeydown(event) {
    const isRtl = getComputedStyle(this).direction === 'rtl';

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.show(this.activeIndex + (isRtl ? 1 : -1), true);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.show(this.activeIndex + (isRtl ? -1 : 1), true);
        break;
      case 'Home':
        event.preventDefault();
        this.show(0, true);
        break;
      case 'End':
        event.preventDefault();
        this.show(this.slides.length - 1, true);
        break;
      default:
        break;
    }
  }

  show(index, announce = true, behavior = null) {
    const nextIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
    this.scrollToIndex(nextIndex, behavior || (this.prefersReducedMotion() ? 'auto' : 'smooth'));
    this.updateState(nextIndex, announce);
  }

  showMedia(mediaId, announce = true) {
    /*
     * Only called for a variant swap, which can jump across several slides
     * at once (e.g. slide 1 straight to slide 5). Smooth-scrolling through
     * every slide in between reads as the gallery jittering through images
     * before landing - jump straight to the target instead.
     */
    if (!mediaId) return;
    const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
    if (index >= 0) this.show(index, announce, 'auto');
  }

  scrollToIndex(index, behavior = 'smooth') {
    const slide = this.slides[index];
    if (!slide) return;

    /*
     * scrollIntoView() also drags the page's own vertical scroll back to
     * this element whenever the gallery isn't currently on screen (e.g.
     * re-initializing after an AJAX variant swap while scrolled down to
     * the sticky add-to-cart bar). Scroll the viewport directly instead
     * so this never touches anything outside the gallery itself.
     */
    this.scrollWithinContainer(this.viewport, slide, behavior, 'x');
  }

  scrollWithinContainer(container, target, behavior, axis = 'both') {
    if (!container || !target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const options = { behavior };

    if (axis !== 'y') {
      options.left = container.scrollLeft + (targetRect.left - containerRect.left);
    }

    if (axis !== 'x') {
      options.top = container.scrollTop + (targetRect.top - containerRect.top);
    }

    container.scrollTo(options);
  }

  updateState(index, announce = true) {
    const nextSlide = this.slides[index];
    if (!nextSlide) return;

    this.activeIndex = index;

    const showsEverySlide = this.isGridStyleLayout && this.desktopQuery.matches;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);

      if (showsEverySlide) {
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('inert');
        return;
      }

      slide.setAttribute('aria-hidden', String(!isActive));
      slide.toggleAttribute('inert', !isActive);

      if (!isActive) this.pauseMedia(slide);
    });

    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const isActive = thumbnailIndex === index;
      thumbnail.classList.toggle('is-active', isActive);
      if (isActive) {
        thumbnail.setAttribute('aria-current', 'true');
        this.scrollWithinContainer(
          this.thumbnailsContainer,
          thumbnail,
          this.prefersReducedMotion() ? 'auto' : 'smooth',
        );
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });

    if (this.previousButton) this.previousButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;

    if (this.xrButton) {
      this.xrButton.dataset.shopifyModel3dId =
        nextSlide.dataset.mediaType === 'model'
          ? nextSlide.dataset.mediaId
          : this.xrButton.dataset.defaultModelId;
    }

    if (announce && this.status) {
      this.status.textContent = nextSlide.getAttribute('aria-label') || '';
    }
  }

  pauseMedia(slide) {
    slide.querySelectorAll('video').forEach((video) => video.pause());
    slide.querySelectorAll('iframe').forEach((iframe) => {
      iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
      iframe.contentWindow?.postMessage({ method: 'pause' }, '*');
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}

class MainProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true' || this.textContent.trim()) {
      this.setAttribute('aria-busy', 'false');
      return;
    }

    const url = this.dataset.recommendationsUrl;
    if (!url) return;

    this.dataset.loaded = 'true';
    this.abortController = new AbortController();
    this.load(url);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  async load(url) {
    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const source = documentFragment.getElementById(this.id);

      if (source?.innerHTML.trim()) {
        this.innerHTML = source.innerHTML;
        this.setAttribute('aria-busy', 'false');
        return;
      }

      this.closest('[data-complementary-block]')?.setAttribute('hidden', '');
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.closest('[data-complementary-block]')?.setAttribute('hidden', '');
      }
    } finally {
      this.setAttribute('aria-busy', 'false');
    }
  }
}

if (!customElements.get('main-product-recommendations')) {
  customElements.define('main-product-recommendations', MainProductRecommendations);
}

(() => {
  if (window.AmerceProductCartSync) return;

  let refreshTimer = null;
  let refreshController = null;

  const getNumber = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const normalizeQuantity = (input, value) => {
    const minimum = getNumber(input.min, 1);
    const maximum = input.max
      ? getNumber(input.max, Number.POSITIVE_INFINITY)
      : Number.POSITIVE_INFINITY;
    const step = Math.max(1, getNumber(input.step, 1));

    let next = getNumber(value, minimum);
    next = Math.max(minimum, next);

    if (Number.isFinite(maximum)) {
      next = Math.min(maximum, next);
    }

    const stepCount = Math.round((next - minimum) / step);
    next = minimum + Math.max(0, stepCount) * step;

    if (Number.isFinite(maximum)) {
      next = Math.min(maximum, next);
    }

    return Math.max(minimum, next);
  };

  const refreshMainProductForm = async (root, signal) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.closest("quick-add-modal")) return;

    const sectionId = root.dataset.sectionId;
    const productUrl = root.dataset.productUrl;
    const currentButtons = root.querySelector(".product-buy-buttons");

    if (!sectionId || !productUrl || !currentButtons) return;

    const currentQuantity =
      root.querySelector("[data-quantity-input]")?.value;

    const requestUrl = new URL(
      productUrl,
      window.location.origin,
    );

    const variantId = root.dataset.selectedVariantId;

    if (variantId) {
      requestUrl.searchParams.set("variant", variantId);
    }

    requestUrl.searchParams.set("section_id", sectionId);

    const response = await fetch(requestUrl.toString(), {
      signal,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Main product refresh failed: ${response.status}`,
      );
    }

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(
      html,
      "text/html",
    );

    const updatedRoot = parsed.getElementById(
      `MainProduct-${sectionId}`,
    );

    const nextButtons = updatedRoot?.querySelector(
      ".product-buy-buttons",
    );

    if (!nextButtons) return;

    const nextQuantityInput = nextButtons.querySelector(
      "[data-quantity-input]",
    );

    if (
      nextQuantityInput &&
      currentQuantity != null &&
      currentQuantity !== ""
    ) {
      nextQuantityInput.value = String(
        normalizeQuantity(
          nextQuantityInput,
          currentQuantity,
        ),
      );
    }

    currentButtons.replaceWith(nextButtons);

    root.syncAllQuantityControls?.();
    root.updateAddToCartPrice?.();

    if (
      typeof window.Shopify?.PaymentButton?.init ===
      "function"
    ) {
      window.Shopify.PaymentButton.init();
    }
  };

  const refreshMainProducts = async () => {
    const roots = Array.from(
      document.querySelectorAll(
        "main-product[data-section-id][data-product-url]",
      ),
    ).filter(
      (root) =>
        !root.closest("quick-add-modal") &&
        root.querySelector(".product-buy-buttons"),
    );

    if (!roots.length) return;

    refreshController?.abort();
    refreshController = new AbortController();

    const results = await Promise.allSettled(
      roots.map((root) =>
        refreshMainProductForm(
          root,
          refreshController.signal,
        ),
      ),
    );

    results.forEach((result) => {
      if (
        result.status === "rejected" &&
        result.reason?.name !== "AbortError"
      ) {
        console.error(result.reason);
      }
    });
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);

    refreshTimer = window.setTimeout(() => {
      refreshMainProducts();
    }, 120);
  };

  ["product:added", "cart:rendered", "cart:updated"].forEach(
    (eventName) => {
      document.addEventListener(
        eventName,
        scheduleRefresh,
      );
    },
  );

  window.AmerceProductCartSync = {
    refresh: refreshMainProducts,
    schedule: scheduleRefresh,
  };
})();

class MainProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mainProductInitialized === 'true') return;

    this.dataset.mainProductInitialized = 'true';

    this.sectionId = this.dataset.sectionId;
    this.abortController = null;
    this.lastDialogTrigger = null;

    this.onChange = (event) => this.handleChange(event);
    this.onInput = (event) => this.handleInput(event);
    this.onClick = (event) => this.handleClick(event);
    this.onDialogClose = (event) => this.handleDialogClose(event);
    this.onOptionSwatchPointerDown = (event) => this.handleOptionSwatchPointerDown(event);

    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerdown', this.onOptionSwatchPointerDown);

    this.querySelectorAll('[data-product-dialog]').forEach((dialog) => {
      dialog.addEventListener('close', this.onDialogClose);
    });

    this.initializeRecipientForms();
    this.initializeModels();
    this.syncAllQuantityControls();
    this.updateAddToCartPrice();
    this.initializeStickyLayout();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.stickyResizeObserver?.disconnect();
    window.clearTimeout(this.quantityMessageTimer);

    this.removeEventListener('change', this.onChange);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerdown', this.onOptionSwatchPointerDown);

    this.querySelectorAll('[data-product-dialog]').forEach((dialog) => {
      dialog.removeEventListener('close', this.onDialogClose);
    });
  }

  /**
   * Sticky info pins the shorter column so it can stay in view while the
   * taller column scrolls past. On most products the gallery is taller than
   * the info column, so the info column is the one that should stick — but a
   * product with few images and a long description flips that, and sticking
   * the (now taller) info column just makes it run off past its own content.
   * Measure both columns and flip `data-sticky-target` to whichever is
   * actually shorter.
   */
  initializeStickyLayout() {
    const layout = this.querySelector('.main-product__layout--sticky-info');
    if (!layout) return;

    const media = layout.querySelector('.main-product__media');
    const info = layout.querySelector('.product-info');
    if (!media || !info) return;

    const updateStickyTarget = () => {
      const isMediaShorter = media.getBoundingClientRect().height < info.getBoundingClientRect().height;
      layout.dataset.stickyTarget = isMediaShorter ? 'media' : 'info';
    };

    this.stickyResizeObserver = new ResizeObserver(updateStickyTarget);
    this.stickyResizeObserver.observe(media);
    this.stickyResizeObserver.observe(info);
  }

  /**
   * The option-value radio/checkbox behind each swatch is visually hidden
   * (`main-product__visually-hidden`) so the styled `<label>` is what the
   * user actually sees and clicks. Clicking that `<label>` makes the browser
   * focus the hidden input as part of its own default action, and because
   * that input isn't currently in the viewport's visible-scroll sense, the
   * browser's native "scroll the newly focused element into view" kicks in
   * and yanks the whole page - visible as a jump the instant a swatch is
   * clicked, well before the AJAX variant swap even starts. Pre-focusing the
   * input ourselves with `preventScroll: true` on pointerdown (which fires
   * before the label's click default action) makes the browser's later
   * focus a no-op, since the input is already focused - so no scroll.
   */
  handleOptionSwatchPointerDown(event) {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.product-option-value__label');
    const input = label?.control;

    if (input && document.activeElement !== input) {
      input.focus({ preventScroll: true });
    }
  }

  handleChange(event) {
    if (!(event.target instanceof Element)) return;

    const optionInput = event.target.closest(
      '[data-option-value-input]',
    );

    if (optionInput) {
      this.handleOptionChange(optionInput);
      return;
    }

    const recipientToggle = event.target.closest(
      '[data-recipient-toggle]',
    );

    if (recipientToggle) {
      this.toggleRecipientFields(recipientToggle);
      return;
    }

    const quantityInput = event.target.closest(
      '[data-quantity-input]',
    );

    if (quantityInput) {
      this.normalizeQuantity(quantityInput);
      this.syncQuantityButtons(quantityInput);
      this.updateAddToCartPrice();
      this.hideQuantityLimitMessage(quantityInput);
    }
  }

  handleInput(event) {
    if (!(event.target instanceof Element)) return;

    const quantityInput = event.target.closest(
      '[data-quantity-input]',
    );

    if (!quantityInput) return;

    this.syncQuantityButtons(quantityInput);
    this.updateAddToCartPrice();
    this.hideQuantityLimitMessage(quantityInput);
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const minusButton = event.target.closest(
      '[data-quantity-minus]',
    );

    if (minusButton) {
      event.preventDefault();
      this.changeQuantity(minusButton, -1);
      return;
    }

    const plusButton = event.target.closest(
      '[data-quantity-plus]',
    );

    if (plusButton) {
      event.preventDefault();
      this.changeQuantity(plusButton, 1);
      return;
    }

    const shareButton = event.target.closest(
      '[data-product-share]',
    );

    if (shareButton) {
      event.preventDefault();
      this.shareProduct(shareButton);
      return;
    }

    const dialogOpen = event.target.closest(
      '[data-dialog-open]',
    );

    if (dialogOpen) {
      event.preventDefault();
      this.openDialog(dialogOpen);
      return;
    }

    const dialogClose = event.target.closest(
      '[data-dialog-close]',
    );

    if (dialogClose) {
      event.preventDefault();
      dialogClose.closest('dialog')?.close();
      return;
    }

    const dialog = event.target.closest(
      '[data-product-dialog]',
    );

    if (dialog && event.target === dialog) {
      dialog.close();
    }
  }

  async handleOptionChange(input) {
    const selectedOption =
      input.tagName === 'SELECT'
        ? input.selectedOptions[0]
        : input;

    const mediaId = selectedOption?.dataset.mediaId;

    const productUrl =
      selectedOption?.dataset.productUrl ||
      this.dataset.productUrl;

    const optionValueIds =
      this.getSelectedOptionValueIds();

    this.querySelector('product-gallery')?.showMedia(
      mediaId,
      false,
    );

    if (!productUrl || optionValueIds.length === 0) {
      return;
    }

    const focusState = {
      tagName: input.tagName,
      position: input.dataset.optionPosition,
      valueId:
        selectedOption?.dataset.optionValueId ||
        input.dataset.optionValueId,
    };

    const quantity =
      this.querySelector('[data-quantity-input]')?.value;

    this.abortController?.abort();
    this.abortController = new AbortController();

    this.setLoading(true);
    this.setStatus(
      this.dataset.loadingText || 'Loading',
    );

    try {
      const requestUrl = new URL(
        productUrl,
        window.location.origin,
      );

      requestUrl.searchParams.delete('variant');

      requestUrl.searchParams.set(
        'option_values',
        optionValueIds.join(','),
      );

      requestUrl.searchParams.set(
        'section_id',
        this.sectionId,
      );

      const response = await fetch(
        requestUrl.toString(),
        {
          signal: this.abortController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Product section request failed: ${response.status}`,
        );
      }

      const html = await response.text();

      const documentFragment =
        new DOMParser().parseFromString(
          html,
          'text/html',
        );

      const updatedRoot =
        documentFragment.getElementById(
          `MainProduct-${this.sectionId}`,
        );

      if (!updatedRoot) {
        throw new Error(
          'Updated main product section was not found',
        );
      }

      this.restoreQuantity(updatedRoot, quantity);
      this.restoreStickyCartVisibility(updatedRoot);

      this.updateBrowserUrl(
        updatedRoot,
        optionValueIds,
      );

      this.swapInUpdatedRoot(updatedRoot);

      this.reinitializeShopifyPaymentButtons();
      this.restoreOptionFocus(
        updatedRoot,
        focusState,
      );

      updatedRoot.dispatchEvent(
        new CustomEvent('product:variant-change', {
          bubbles: true,
          detail: {
            variantId:
              updatedRoot.dataset.selectedVariantId ||
              null,
            productUrl:
              updatedRoot.dataset.productUrl,
          },
        }),
      );

      requestAnimationFrame(() => {
        updatedRoot.syncAllQuantityControls?.();
        updatedRoot.updateAddToCartPrice?.();

        updatedRoot.setStatus?.(
          updatedRoot.dataset.updatedText ||
            'Updated',
        );
      });
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(error);

      this.setLoading(false);

      this.setStatus(
        this.dataset.errorText ||
          'Unable to update this product',
      );
    }
  }

  getSelectedOptionValueIds() {
    const selected = new Map();

    this.querySelectorAll(
      '[data-option-value-input]',
    ).forEach((field) => {
      const position = Number(
        field.dataset.optionPosition,
      );

      if (!position) return;

      if (field.tagName === 'SELECT') {
        const option = field.selectedOptions[0];

        if (option?.dataset.optionValueId) {
          selected.set(
            position,
            option.dataset.optionValueId,
          );
        }

        return;
      }

      if (
        field.checked &&
        field.dataset.optionValueId
      ) {
        selected.set(
          position,
          field.dataset.optionValueId,
        );
      }
    });

    return Array.from(selected.entries())
      .sort(
        ([positionA], [positionB]) =>
          positionA - positionB,
      )
      .map(([, valueId]) => valueId);
  }

  updateBrowserUrl(updatedRoot, optionValueIds) {
    if (updatedRoot.dataset.updateUrl !== 'true') {
      return;
    }

    const url = new URL(
      updatedRoot.dataset.productUrl,
      window.location.origin,
    );

    const variantId =
      updatedRoot.dataset.selectedVariantId;

    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else if (optionValueIds.length > 0) {
      url.searchParams.set(
        'option_values',
        optionValueIds.join(','),
      );
    }

    window.history.replaceState(
      {},
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }

  restoreQuantity(updatedRoot, quantity) {
    if (quantity == null || quantity === '') return;

    const input = updatedRoot.querySelector(
      '[data-quantity-input]',
    );

    if (!input) return;

    input.value = quantity;

    /*
     * Không gọi method của updatedRoot ở đây vì element
     * lấy từ DOMParser có thể chưa được upgrade.
     */
    this.normalizeQuantity(input);
  }

  restoreStickyCartVisibility(updatedRoot) {
    /*
     * replaceWith() tears down and recreates <sticky-add-to-cart> from
     * scratch, so it always starts hidden again and only reappears once
     * its own IntersectionObserver fires on a later frame — visible as a
     * blink even though the user never actually scrolled past the trigger
     * again. Carry the visible state over immediately so there's no gap.
     */
    const wasVisible = this.querySelector('sticky-add-to-cart')?.classList.contains('is-visible');
    if (!wasVisible) return;

    const sticky = updatedRoot.querySelector('sticky-add-to-cart');
    if (!sticky) return;

    sticky.hidden = false;
    sticky.classList.add('is-visible');
    sticky.setAttribute('aria-hidden', 'false');
  }

  preserveGalleryElement(updatedRoot) {
    /*
     * replaceWith() also tears down and recreates <product-gallery> from
     * scratch. showMedia() already scrolled the CURRENT gallery to the
     * variant's image the instant the option changed, but the freshly
     * parsed markup starts unscrolled and only reaches the right slide on
     * its own next animation frame — visible as the image flashing back to
     * the first slide before snapping to the correct one. Move the live,
     * already-correct gallery into the incoming markup instead of letting
     * the fetched copy replace it, so nothing resets.
     */
    const currentMedia = this.querySelector('.main-product__media');
    const nextMedia = updatedRoot.querySelector('.main-product__media');
    if (!currentMedia || !nextMedia) return;

    nextMedia.replaceWith(currentMedia);
  }

  swapInUpdatedRoot(updatedRoot) {
    /*
     * A plain replaceWith() snaps the whole buy-box (price, availability,
     * badges) to its new state in one frame, which reads as a jolt even
     * though the content itself is correct. Wrap it in a View Transition
     * so the browser cross-fades old and new state instead of a hard cut
     * - same technique reference storefronts use for variant swaps. Falls
     * back to the plain swap on unsupported browsers or reduced motion.
     */
    const swap = () => {
      /*
       * Must happen inside this callback, not before startViewTransition()
       * is called: that call snapshots the CURRENT live DOM synchronously
       * as the "old" frame. Hoisting the gallery any earlier would remove
       * it from the live tree before that snapshot is taken, so the
       * transition would fade from "no gallery" to "gallery" - visible as
       * the gallery vanishing and popping back in.
       */
      this.preserveGalleryElement(updatedRoot);

      const scrollY = window.scrollY;
      this.replaceWith(updatedRoot);
      window.scrollTo(0, scrollY);
    };

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (!prefersReducedMotion && typeof document.startViewTransition === 'function') {
      document.startViewTransition(swap);
    } else {
      swap();
    }
  }

  restoreOptionFocus(updatedRoot, focusState) {
    requestAnimationFrame(() => {
      let target = null;

      if (focusState.tagName === 'SELECT') {
        target = updatedRoot.querySelector(
          `select[data-option-position="${this.escapeSelector(
            focusState.position,
          )}"]`,
        );
      } else if (focusState.valueId) {
        target = updatedRoot.querySelector(
          `[data-option-value-input][data-option-value-id="${this.escapeSelector(
            focusState.valueId,
          )}"]`,
        );
      }

      target?.focus({
        preventScroll: true,
      });
    });
  }

  escapeSelector(value) {
    if (window.CSS?.escape) {
      return CSS.escape(String(value ?? ''));
    }

    return String(value ?? '').replace(
      /["\\]/g,
      '\\$&',
    );
  }

  setLoading(isLoading) {
    this.classList.toggle(
      'is-loading',
      isLoading,
    );

    this.setAttribute(
      'aria-busy',
      String(isLoading),
    );
  }

  setStatus(message) {
    const status = this.querySelector(
      '[data-product-status]',
    );

    if (!status) return;

    status.textContent = '';

    requestAnimationFrame(() => {
      status.textContent = message || '';
    });
  }

  changeQuantity(button, direction) {
    const control = button.closest(
      '.quantity-selector__control',
    );

    const input = control?.querySelector(
      '[data-quantity-input]',
    );

    if (!input) return;

    if (button.getAttribute('aria-disabled') === 'true') {
      // Already at the limit - the click had no effect, which reads as a
      // bug unless we say why.
      this.showQuantityLimitMessage(input, direction);
      return;
    }

    const bounds = this.getQuantityBounds(input);

    const currentValue = this.getQuantityNumber(
      input.value,
      bounds.minimum,
    );

    let nextValue =
      currentValue + bounds.step * direction;

    nextValue = Math.max(
      bounds.minimum,
      nextValue,
    );

    if (Number.isFinite(bounds.maximum)) {
      nextValue = Math.min(
        bounds.maximum,
        nextValue,
      );
    }

    input.value = String(nextValue);

    this.normalizeQuantity(input);
    this.syncQuantityButtons(input);
    this.updateAddToCartPrice();

    if (
      direction > 0 &&
      Number.isFinite(bounds.maximum) &&
      nextValue >= bounds.maximum
    ) {
      this.showQuantityLimitMessage(input, direction);
    } else {
      this.hideQuantityLimitMessage(input);
    }

    input.dispatchEvent(
      new Event('change', {
        bubbles: true,
      }),
    );
  }

  showQuantityLimitMessage(input, direction) {
    const message = input
      .closest('.quantity-selector, .sticky-add-to-cart__quantity')
      ?.querySelector('[data-quantity-message], [data-sticky-quantity-message]');

    if (!message) return;

    if (direction < 0) {
      message.hidden = true;
      return;
    }

    const bounds = this.getQuantityBounds(input);

    if (!Number.isFinite(bounds.maximum)) {
      message.hidden = true;
      return;
    }

    const template = message.dataset.quantityMessageTemplate;

    message.textContent = template
      ? template.replace('[quantity]', String(bounds.maximum))
      : String(bounds.maximum);

    message.hidden = false;

    window.clearTimeout(this.quantityMessageTimer);
    this.quantityMessageTimer = window.setTimeout(() => {
      message.hidden = true;
    }, 3000);
  }

  hideQuantityLimitMessage(input) {
    const message = input
      .closest('.quantity-selector, .sticky-add-to-cart__quantity')
      ?.querySelector('[data-quantity-message], [data-sticky-quantity-message]');

    if (message) {
      message.hidden = true;
    }

    window.clearTimeout(this.quantityMessageTimer);
  }

  getQuantityBounds(input) {
    const minimum = this.getQuantityNumber(
      input.min,
      1,
    );

    const maximum =
      input.max !== ''
        ? this.getQuantityNumber(
            input.max,
            Number.POSITIVE_INFINITY,
          )
        : Number.POSITIVE_INFINITY;

    const step = Math.max(
      1,
      this.getQuantityNumber(input.step, 1),
    );

    return {
      minimum,
      maximum,
      step,
    };
  }

  getQuantityNumber(value, fallback) {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : fallback;
  }

  normalizeQuantity(input) {
    const bounds = this.getQuantityBounds(input);

    let value = this.getQuantityNumber(
      input.value,
      bounds.minimum,
    );

    value = Math.max(
      bounds.minimum,
      value,
    );

    if (Number.isFinite(bounds.maximum)) {
      value = Math.min(
        bounds.maximum,
        value,
      );
    }

    const stepCount = Math.round(
      (value - bounds.minimum) / bounds.step,
    );

    value =
      bounds.minimum +
      Math.max(0, stepCount) * bounds.step;

    if (Number.isFinite(bounds.maximum)) {
      value = Math.min(
        bounds.maximum,
        value,
      );
    }

    value = Math.max(
      bounds.minimum,
      value,
    );

    input.value = String(value);

    return value;
  }

  syncQuantityButtons(input) {
    const control = input.closest(
      '.quantity-selector__control',
    );

    if (!control) return;

    const bounds = this.getQuantityBounds(input);

    const currentValue = this.getQuantityNumber(
      input.value,
      bounds.minimum,
    );

    const minusButton = control.querySelector(
      '[data-quantity-minus]',
    );

    const plusButton = control.querySelector(
      '[data-quantity-plus]',
    );

    if (minusButton) {
      const disableMinus =
        currentValue <= bounds.minimum;

      // aria-disabled only (not the native disabled attribute): a truly
      // disabled button never dispatches click events, which would make
      // changeQuantity's limit-message branch unreachable.
      minusButton.setAttribute(
        'aria-disabled',
        String(disableMinus),
      );
    }

    if (plusButton) {
      const disablePlus =
        Number.isFinite(bounds.maximum) &&
        currentValue >= bounds.maximum;

      plusButton.setAttribute(
        'aria-disabled',
        String(disablePlus),
      );
    }
  }

  syncAllQuantityControls() {
    this.querySelectorAll(
      '.quantity-selector__control [data-quantity-input]',
    ).forEach((input) => {
      this.normalizeQuantity(input);
      this.syncQuantityButtons(input);
    });
  }

  updateAddToCartPrice() {
    const prices = this.querySelectorAll(
      '[data-add-to-cart-price]',
    );

    if (prices.length === 0) return;

    const quantityInput = this.querySelector(
      '[data-quantity-input]',
    );

    const quantity = Math.max(
      1,
      Number(quantityInput?.value) || 1,
    );

    prices.forEach((price) => {
      const unitPrice = Number(
        price.dataset.unitPrice,
      );

      if (!Number.isFinite(unitPrice)) return;

      price.textContent = this.formatMoney(
        Math.round(unitPrice * quantity),
        price.dataset.moneyFormat || '${{amount}}',
      );
    });
  }

  formatMoney(cents, format) {
    const placeholder =
      /\{\{\s*(\w+)\s*\}\}/;

    const match = format.match(placeholder);

    if (!match) {
      return String(cents / 100);
    }

    const formatNumber = (
      value,
      precision = 2,
      thousands = ',',
      decimal = '.',
    ) => {
      const number = (value / 100).toFixed(
        precision,
      );

      const [integer, fraction] =
        number.split('.');

      const grouped = integer.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        thousands,
      );

      return precision === 0
        ? grouped
        : `${grouped}${decimal}${fraction}`;
    };

    let value;

    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatNumber(cents, 0);
        break;

      case 'amount_with_comma_separator':
        value = formatNumber(
          cents,
          2,
          '.',
          ',',
        );
        break;

      case 'amount_no_decimals_with_comma_separator':
        value = formatNumber(
          cents,
          0,
          '.',
          ',',
        );
        break;

      case 'amount_with_apostrophe_separator':
        value = formatNumber(
          cents,
          2,
          "'",
          '.',
        );
        break;

      case 'amount_no_decimals_with_space_separator':
        value = formatNumber(
          cents,
          0,
          ' ',
        );
        break;

      case 'amount_with_space_separator':
        value = formatNumber(
          cents,
          2,
          ' ',
          ',',
        );
        break;

      case 'amount':
      default:
        value = formatNumber(cents, 2);
        break;
    }

    return format.replace(
      placeholder,
      value,
    );
  }

  initializeRecipientForms() {
    this.querySelectorAll(
      '[data-recipient-toggle]',
    ).forEach((toggle) => {
      this.toggleRecipientFields(
        toggle,
        false,
      );
    });
  }

  toggleRecipientFields(
    toggle,
    moveFocus = true,
  ) {
    const recipient = toggle.closest(
      '[data-recipient-form]',
    );

    const fieldsContainer =
      recipient?.querySelector(
        '[data-recipient-fields]',
      );

    if (!fieldsContainer) return;

    const enabled = toggle.checked;

    fieldsContainer.hidden = !enabled;

    fieldsContainer
      .querySelectorAll(
        '[data-recipient-field]',
      )
      .forEach((field) => {
        field.disabled = !enabled;
      });

    const offset =
      fieldsContainer.querySelector(
        '[data-recipient-offset]',
      );

    if (offset) {
      offset.disabled = !enabled;

      offset.value = enabled
        ? String(
            new Date().getTimezoneOffset(),
          )
        : '';
    }

    const dateInput =
      fieldsContainer.querySelector(
        '[data-recipient-date]',
      );

    if (dateInput) {
      this.configureRecipientDate(dateInput);
    }

    if (enabled && moveFocus) {
      fieldsContainer
        .querySelector(
          'input:not([type="hidden"]), textarea',
        )
        ?.focus();
    }
  }

  configureRecipientDate(input) {
    const today = new Date();
    const maximum = new Date(today);

    maximum.setDate(
      maximum.getDate() + 90,
    );

    input.min = this.formatDate(today);
    input.max = this.formatDate(maximum);
  }

  formatDate(date) {
    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1,
    ).padStart(2, '0');

    const day = String(
      date.getDate(),
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  openDialog(trigger) {
    const dialog = this.querySelector(
      `#${this.escapeSelector(
        trigger.dataset.dialogOpen,
      )}`,
    );

    if (!dialog) return;

    this.lastDialogTrigger = trigger;

    dialog.showModal();

    dialog
      .querySelector('[data-dialog-close]')
      ?.focus();
  }

  handleDialogClose() {
    this.lastDialogTrigger?.focus();
    this.lastDialogTrigger = null;
  }

  async shareProduct(button) {
    const shareData = {
      title:
        button.dataset.shareTitle ||
        document.title,
      url:
        button.dataset.shareUrl ||
        window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(
          shareData.url,
        );
      } else {
        return;
      }

      this.setStatus(
        this.dataset.shareSuccessText ||
          'Link copied',
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }
  }

  reinitializeShopifyPaymentButtons() {
    if (
      typeof window.Shopify?.PaymentButton
        ?.init === 'function'
    ) {
      window.Shopify.PaymentButton.init();
    }
  }

  initializeModels() {
    if (!this.querySelector('model-viewer')) {
      return;
    }

    if (!window.Shopify?.loadFeatures) {
      return;
    }

    const modelData = this.querySelector(
      '[data-product-model-json]',
    );

    let models = [];

    try {
      models = JSON.parse(
        modelData?.textContent || '[]',
      );
    } catch (error) {
      console.error(error);
    }

    const features = [
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (error) => {
          if (error) console.error(error);
        },
      },
    ];

    if (models.length > 0) {
      features.push({
        name: 'shopify-xr',
        version: '1.0',
        onLoad: (error) => {
          if (error) {
            console.error(error);
            return;
          }

          this.setupShopifyXr(models);
        },
      });
    }

    window.Shopify.loadFeatures(features);
  }

  setupShopifyXr(models) {
    if (!window.ShopifyXR) {
      document.addEventListener(
        'shopify_xr_initialized',
        () => this.setupShopifyXr(models),
        {
          once: true,
        },
      );

      return;
    }

    window.ShopifyXR.addModels(models);
    window.ShopifyXR.setupXRElements();
  }
}

if (!customElements.get('main-product')) {
  customElements.define(
    'main-product',
    MainProduct,
  );
}
})();
